import React, { useState, useEffect } from "react";
import { Calendar, CheckCircle, ChevronLeft, ChevronRight } from "lucide-react";

// ─── Timeline de reclamaciones ────────────────────────────────────────
const VISIBLE_RECLAMACIONES = 4;

// Texto relativo tipo "hace 3 meses" / "en 2 meses" / "hace 5 días"
const tiempoRelativo = (date) => {
  const ms = date.getTime() - Date.now();
  const futuro = ms > 0;
  const pre = futuro ? 'en' : 'hace';
  const dias = Math.floor(Math.abs(ms) / 86400000);
  if (dias === 0) return 'hoy';
  if (dias < 30) return `${pre} ${dias} ${dias === 1 ? 'día' : 'días'}`;
  const meses = Math.floor(dias / 30.44);
  if (meses < 12) return `${pre} ${meses} ${meses === 1 ? 'mes' : 'meses'}`;
  const anios = Math.floor(meses / 12);
  const resto = meses % 12;
  let txt = `${anios} ${anios === 1 ? 'año' : 'años'}`;
  if (resto > 0) txt += ` y ${resto} ${resto === 1 ? 'mes' : 'meses'}`;
  return `${pre} ${txt}`;
};

const ReclamacionesTimeline = ({ fechas, loading, t, accent = "#92004D", accentLight = "#c980a6" }) => {
  const vistos = new Set();
  const items = (fechas || [])
    .map((f) => new Date(f))
    .filter((d) => !isNaN(d))
    .sort((a, b) => a - b)
    // Dedupe por día LOCAL (lo que se muestra), evita fechas repetidas por desfase horario
    .filter((d) => {
      const key = d.toDateString();
      if (vistos.has(key)) return false;
      vistos.add(key);
      return true;
    });

  const total = items.length;
  const pageCount = Math.max(1, Math.ceil(total / VISIBLE_RECLAMACIONES));
  const [page, setPage] = useState(0);
  const [dir, setDir] = useState(1); // 1 = avanzar (entra por la derecha), -1 = retroceder (por la izquierda)
  const [hoverIdx, setHoverIdx] = useState(null);
  const [hoverPrev, setHoverPrev] = useState(false);
  const [hoverNext, setHoverNext] = useState(false);

  // Al cargar/cambiar las fechas, mostrar por defecto la última página (las más recientes)
  useEffect(() => {
    setPage(pageCount - 1);
  }, [total]);

  // Páginas alineadas al FINAL: la última página (por defecto) muestra siempre las 4 más recientes;
  // el resto parcial queda en la primera página (las más antiguas).
  const fin = total - (pageCount - 1 - page) * VISIBLE_RECLAMACIONES;
  const inicio = Math.max(0, fin - VISIBLE_RECLAMACIONES);
  const visibles = items.slice(inicio, fin);
  const canPrev = page > 0;
  const canNext = page < pageCount - 1;
  const showNav = pageCount > 1; // solo si hay más fechas que el límite por página

  const btnStyle = (enabled, hovered) => ({
    transform: 'translateY(-13px)',
    cursor: enabled ? 'pointer' : 'default',
    color: !enabled ? '#e5e7eb' : hovered ? accent : '#6b7280',
    borderColor: !enabled ? '#e5e7eb' : hovered ? accent : '#d1d5db',
  });

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 min-w-0">
      <style>{`
        @keyframes reclFadeRight { from { opacity: 0; transform: translateX(48px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes reclFadeLeft { from { opacity: 0; transform: translateX(-48px); } to { opacity: 1; transform: translateX(0); } }
      `}</style>
      <div className="flex items-center mb-4">
        <Calendar className="mr-2 mb-1 w-5 h-5" strokeWidth={1.5} style={{ color: accent }} />
        <h2 className="text-base font-bold text-gray-900">
          {t('detail.reclamationHistory', { defaultValue: 'Histórico de reclamaciones' })}
        </h2>
        {total > 0 && (
          <span className="text-xs text-gray-400 ml-2 mb-0.5">({total})</span>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-gray-400">{t('common:loading', { defaultValue: 'Cargando...' })}</p>
      ) : total === 0 ? (
        <p className="text-sm text-gray-400">{t('detail.noReclamations', { defaultValue: 'Sin reclamaciones' })}</p>
      ) : (
        <div className="flex items-center gap-2">
          {showNav && (
            <button
              type="button"
              className="flex items-center justify-center w-8 h-8 rounded-full border flex-shrink-0 transition-colors"
              style={btnStyle(canPrev, hoverPrev)}
              disabled={!canPrev}
              onMouseEnter={() => setHoverPrev(true)}
              onMouseLeave={() => setHoverPrev(false)}
              onClick={() => { if (canPrev) { setDir(-1); setPage((p) => Math.max(0, p - 1)); } }}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          )}

          <div className="relative flex-1 min-w-0">
            {/* línea continua detrás de los puntos visibles */}
            {visibles.length > 1 && (
              <div
                className="absolute"
                style={{ top: 7, height: 2, backgroundColor: '#d1d5db', left: `${50 / VISIBLE_RECLAMACIONES}%`, width: `${((visibles.length - 1) * 100) / VISIBLE_RECLAMACIONES}%` }}
              />
            )}
            <div key={page} className="relative flex" style={{ animation: `${dir === 1 ? 'reclFadeRight' : 'reclFadeLeft'} 450ms cubic-bezier(0.22, 1, 0.36, 1)` }}>
              {visibles.map((d, i) => {
                const idx = inicio + i;
                const isLast = idx === total - 1;
                const pos = idx + 1;
                const hovered = hoverIdx === idx;
                return (
                  <div
                    key={idx}
                    className="relative flex flex-col items-center min-w-0 cursor-default"
                    style={{ width: `${100 / VISIBLE_RECLAMACIONES}%` }}
                    onMouseEnter={() => setHoverIdx(idx)}
                    onMouseLeave={() => setHoverIdx(null)}
                  >
                    <div
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: '9999px',
                        backgroundColor: isLast ? accent : accentLight,
                        boxShadow: '0 0 0 2px #ffffff',
                        position: 'relative',
                        zIndex: 1,
                        transition: 'transform 150ms ease',
                        transform: hovered ? 'scale(1.3)' : 'none',
                      }}
                    />
                    <span
                      className="mt-2 text-xs whitespace-nowrap"
                      style={isLast ? { color: accent, fontWeight: 600 } : { color: '#6b7280' }}
                    >
                      {d.toLocaleDateString()}
                    </span>

                    {/* Tooltip al hover */}
                    <div
                      className="pointer-events-none absolute bottom-full left-1/2 mb-3 z-30"
                      style={{
                        transform: `translateX(-50%) translateY(${hovered ? '0' : '4px'})`,
                        opacity: hovered ? 1 : 0,
                        transition: 'opacity 150ms ease, transform 150ms ease',
                      }}
                    >
                      <div className="whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 py-2 text-center shadow-lg">
                        <p className="flex items-center justify-center gap-1 text-xs font-medium text-gray-700">
                          <CheckCircle className="w-3.5 h-3.5 text-green-500" strokeWidth={2} />
                          {t('detail.notificationSent', { defaultValue: 'Notificación enviada' })}
                        </p>
                        <p className="text-[11px] font-semibold mt-0.5" style={{ color: accent }}>
                          {pos} de {total}{isLast ? ' · Última' : ''}
                        </p>
                        <p className="text-[11px] text-gray-400">{tiempoRelativo(d)}</p>
                      </div>
                      <div className="absolute left-1/2 -bottom-1 h-2 w-2 -translate-x-1/2 rotate-45 border-b border-r border-gray-200 bg-white" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {showNav && (
            <button
              type="button"
              className="flex items-center justify-center w-8 h-8 rounded-full border flex-shrink-0 transition-colors"
              style={btnStyle(canNext, hoverNext)}
              disabled={!canNext}
              onMouseEnter={() => setHoverNext(true)}
              onMouseLeave={() => setHoverNext(false)}
              onClick={() => { if (canNext) { setDir(1); setPage((p) => Math.min(pageCount - 1, p + 1)); } }}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ReclamacionesTimeline;
